import {
  connectorTokenStatusResponseSchema,
  mintConnectorTokenResponseSchema,
  type ConnectorTokenStatusResponse,
} from '@awwyeah/shared';

/**
 * REST client for the WF-1.2 Claude connector token's lifecycle. All three calls are
 * session-authed (`credentials: 'include'`, same as `calendar/feedApi.ts`). There is no
 * client-side wrapper for the connector endpoint itself (`/api/mcp/c/<token>`) — that's a
 * URL handed to Claude, never fetched by this app.
 */

export async function fetchConnectorTokenStatus(): Promise<ConnectorTokenStatusResponse> {
  const res = await fetch('/api/mcp/connector-token', { credentials: 'include' });
  if (!res.ok) throw new Error(`connector token status failed: ${res.status}`);
  return connectorTokenStatusResponseSchema.parse(await res.json());
}

/** Mints (or regenerates — the server revokes any prior token) and returns the full raw
 * token. This is the only response that ever carries it. */
export async function mintConnectorToken(): Promise<string> {
  const res = await fetch('/api/mcp/connector-token', {
    method: 'POST',
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`connector token mint failed: ${res.status}`);
  return mintConnectorTokenResponseSchema.parse(await res.json()).token;
}

export async function revokeConnectorToken(): Promise<void> {
  const res = await fetch('/api/mcp/connector-token', {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`connector token revoke failed: ${res.status}`);
}

/** Builds the full connector URL from a freshly minted raw token — same-origin, so this
 * works identically in dev (Vite proxy) and prod (behind Caddy). This is the exact URL
 * Claude → Settings → Connectors → Add custom connector expects pasted in. */
export function connectorUrlFromToken(token: string): string {
  return `${window.location.origin}/api/mcp/c/${token}`;
}
